import _ from 'lodash'
import {
	CompGridData,
	Component,
	CompsGridDataMap,
	DataForMesh,
	FlatMeshData,
	GridOverflow,
	Mesh,
	MeshDataOptions,
	MeshHierarchyFlat,
	MeshHierarchyStructure,
	MeshMarginsOptions,
	MeshOptions,
	MeshStyles,
	NearestTopTopPusher,
	NegativeBottomTops,
	VerticalArea,
} from './Types'

import { getComponentBoundingBox } from './structure2mesh/boundingLayout'
import calculateCompsGridData from './structure2mesh/calculateCompsGridData'
import calculateGridPoints from './structure2mesh/calculateGridPoints'
import {
	bottom,
	fitToContentHeight,
	getContainerHeight,
	height,
	isRotated,
	top,
} from './structure2mesh/compLayoutUtils'
import {
	getCompAbsoluteStyle,
	getCompStyles,
	getEmptyContainerStyles,
	getGridContainerStyles,
	getGridWrapperStyle,
	getRootContainerStyle,
	getRotatedComponentStyle,
	getRotatedComponentWrapperStyle,
	getWedgeStyle,
	unitize,
} from './structure2mesh/meshCssUtils'
import { RowsLimitError } from './structure2mesh/rowsAPIFactory'
// @ts-ignore
const getGridWrapperId = (containerId) => `${containerId}-gridWrapper`
// @ts-ignore
const getGridContainerId = (containerId) => `${containerId}-gridContainer`

const getGridVerticalArea = (container: Component, compsMinTop: number, compsMaxBottom: number): VerticalArea => {
	const areaTop = _.min([0, compsMinTop])
	const areaBottom = _.max([container.height || 0, container.minHeight || 0, compsMaxBottom])
	return {
		// @ts-ignore
		top: areaTop,
		// @ts-ignore
		bottom: areaBottom,
	}
}

const childrenContainerMinHeight = (
	comp: Component,
	gridComponents: Array<Component>,
	mostBottom: number,
	gridOverflow: VerticalArea,
	{ minLockMargin }: MeshMarginsOptions
): string => {
	const overflowTop = _.get(gridOverflow, 'top', 0)

	if (fitToContentHeight(comp)) {
		return _.isUndefined(comp.minHeight) ? 'auto' : unitize(comp.minHeight + overflowTop)
	}
	// @ts-ignore
	const isSoft = comp.height - mostBottom > minLockMargin

	if (_.isEmpty(gridComponents) || isSoft) {
		// @ts-ignore
		return unitize(_.max([comp.height, comp.minHeight]) + overflowTop)
	}

	return _.isUndefined(comp.minHeight) ? 'auto' : unitize(comp.minHeight + overflowTop)
}

const getGridOverflow = (container: Component, gridVerticalArea: VerticalArea): GridOverflow => {
	const overflowFromTop = 0 - gridVerticalArea.top
	const overflowFromBottom = fitToContentHeight(container)
		? 0
		: // @ts-ignore
		  gridVerticalArea.bottom - getContainerHeight(container)

	if (!overflowFromTop && !overflowFromBottom) {
		// @ts-ignore
		return null
	}

	return {
		top: overflowFromTop,
		bottom: overflowFromBottom,
	}
}

const getRootStructure: (meshHierarchy: MeshHierarchyFlat, containerId: string) => MeshHierarchyStructure = (
	meshHierarchy,
	containerId
) => {
	const getCompStructure: (compId: string, childrenIds: Array<string>) => MeshHierarchyStructure = (
		compId,
		childrenIds = []
	) => ({
		id: compId,
		children: _.map(childrenIds, (childId) => getCompStructure(childId, meshHierarchy[childId])),
	})

	return getCompStructure(containerId, meshHierarchy[containerId])
}
// @ts-ignore
const addChildToMesh = (containerMeshData: FlatMeshData, { style, id }): FlatMeshData => {
	containerMeshData.hierarchy[getGridContainerId(containerMeshData.id)].push(id)
	containerMeshData.styles[id] = style
	return containerMeshData
}

const addComponentsToMeshData = (
	container: Component,
	containerMeshData: FlatMeshData,
	components: Array<Component>,
	compsGridData: CompsGridDataMap,
	gridVerticalArea: VerticalArea,
	meshOptions: MeshDataOptions,
	// @ts-ignore
	negativeBottomTops
) => {
	// @ts-ignore
	const compsStyles = []
	_(components).forEach((child) => {
		if (child.absolute) {
			const style = getCompAbsoluteStyle(child, container)
			compsStyles.push({
				id: child.id,
				style,
			})
		} else if (isRotated(child)) {
			addRotatedComponentAndWrapper(
				// @ts-ignore
				compsStyles,
				container,
				child,
				containerMeshData,
				// @ts-ignore
				compsGridData[child.id],
				gridVerticalArea,
				meshOptions,
				negativeBottomTops
			)
		} else {
			const style = getCompStyles(
				child,
				// @ts-ignore
				compsGridData[child.id],
				gridVerticalArea,
				container,
				meshOptions,
				negativeBottomTops
			)
			compsStyles.push({
				id: child.id,
				style,
			})
		}
	})
	// @ts-ignore
	_.forEach(compsStyles, addChildToMesh.bind(null, containerMeshData))
}

const addRotatedComponentAndWrapper = (
	// @ts-ignore
	compsStyles,
	container: Component,
	child: Component,
	containerMeshData: FlatMeshData,
	compGridData: CompGridData,
	gridVerticalArea: VerticalArea,
	meshOptions: MeshDataOptions,
	// @ts-ignore
	negativeBottomTops
) => {
	const wrapperStyle = getRotatedComponentWrapperStyle(
		child,
		container,
		compGridData,
		gridVerticalArea,
		meshOptions,
		negativeBottomTops
	)
	const wrapperId = `${child.id}-rotated-wrapper`
	const nonRotatedId = `${child.id}-non-rotated`
	const childStyle = getRotatedComponentStyle(child)
	compsStyles.push({
		id: wrapperId,
		style: wrapperStyle,
	})
	// @ts-ignore
	containerMeshData.hierarchy[wrapperId] = meshOptions.shouldAddNonRotatedComponents
		? [nonRotatedId, child.id]
		: [child.id]
	// @ts-ignore
	containerMeshData.styles[child.id] = childStyle
	if (meshOptions.shouldAddNonRotatedComponents) {
		containerMeshData.styles[nonRotatedId] = childStyle
	}
}

const addWedgesToMeshData = (
	containerMeshData: FlatMeshData,
	compsGridData: CompsGridDataMap,
	{ cssGridVariant }: MeshDataOptions
) => {
	const ie = cssGridVariant === 'ms'

	_(compsGridData)
		.pickBy((compGridData) => compGridData.isWedge)
		.map((wedgeGridData: CompGridData) => ({
			id: `${containerMeshData.id}-${wedgeGridData.id}`,
			style: getWedgeStyle(wedgeGridData, ie),
		}))
		// @ts-ignore
		.reduce(addChildToMesh, containerMeshData)
}

const createContainerMeshData = (
	container: Component,
	numberOfRows: number,
	containerMinHeight: string,
	gridOverflow: GridOverflow,
	{ cssGridVariant, isRepeatSupported }: MeshDataOptions
): FlatMeshData => {
	const containerId = container.id
	const gridWrapperId = getGridWrapperId(containerId)
	const gridContentId = getGridContainerId(containerId)
	const hierarchy: MeshHierarchyFlat = {
		// @ts-ignore
		[containerId]: [gridWrapperId],
		[gridWrapperId]: [gridContentId],
		[gridContentId]: [],
	}

	const containerStyle = getRootContainerStyle(container)
	const gridWrapperStyle = getGridWrapperStyle(gridOverflow, cssGridVariant === 'ms')
	const { fitToContentHeight: _fitToContentHeight = false, fitToContentPadding = 0 } = container
	const gridContainerStyles = getGridContainerStyles(
		// @ts-ignore
		cssGridVariant,
		numberOfRows,
		gridOverflow,
		containerMinHeight,
		_fitToContentHeight,
		fitToContentPadding,
		isRepeatSupported
	)

	const styles: MeshStyles = {
		// @ts-ignore
		[containerId]: containerStyle,
		[gridWrapperId]: gridWrapperStyle,
		[gridContentId]: gridContainerStyles,
	}

	return {
		// @ts-ignore
		id: containerId,
		hierarchy,
		styles,
	}
}

const createEmptyContainerMeshData = (container: Component, containerMinHeight: string): FlatMeshData => {
	const containerId = container.id
	const gridContentId = getGridContainerId(containerId)
	const hierarchy: MeshHierarchyFlat = {
		// @ts-ignore
		[containerId]: [gridContentId],
		[gridContentId]: [],
	}

	const containerStyle = getRootContainerStyle(container)
	const { fitToContentHeight: _fitToContentHeight = false, fitToContentPadding = 0 } = container
	const gridContainerStyles = getEmptyContainerStyles(containerMinHeight, _fitToContentHeight, fitToContentPadding)

	const styles: MeshStyles = {
		// @ts-ignore
		[containerId]: containerStyle,
		[gridContentId]: gridContainerStyles,
	}

	return {
		// @ts-ignore
		id: containerId,
		hierarchy,
		styles,
	}
}

const getCompsPushingData = (
	components: Array<Component>
): { negativeBottomTops: NegativeBottomTops; nearestTopTopPusher: NearestTopTopPusher } =>
	_.reduce(
		components,
		({ negativeBottomTops, nearestTopTopPusher }, comp: Component) => {
			const maxTopTopMap = {}
			const compTop = top(comp)
			const compBottom = bottom(comp)
			const compCenter = compTop + height(comp) / 2

			_.forEach(components, (testedPushedComp: Component) => {
				if (comp === testedPushedComp) {
					return
				}

				const isPushedCompTopOverlappingComp = _.inRange(top(testedPushedComp), compTop + 1, compBottom)
				if (isPushedCompTopOverlappingComp) {
					const isSiblingBottomBelowCompBottom = bottom(testedPushedComp) > compBottom
					if (isSiblingBottomBelowCompBottom) {
						if (_.inRange(top(testedPushedComp), compCenter, compBottom)) {
							// bottom-top
							// @ts-ignore
							negativeBottomTops[comp.id] = _.max([
								compBottom - top(testedPushedComp),
								// @ts-ignore
								negativeBottomTops[comp.id],
							])
						}
					} else {
						// full overlapping between sibling comps
						// top-top
						if (
							height(testedPushedComp) > 0 &&
							// @ts-ignore
							(_.isUndefined(maxTopTopMap[testedPushedComp.id]) ||
								// @ts-ignore
								compTop > maxTopTopMap[testedPushedComp.id])
						) {
							// @ts-ignore
							maxTopTopMap[testedPushedComp.id] = compTop
							// @ts-ignore
							nearestTopTopPusher[testedPushedComp.id] = comp.id
						}
					}
				}
			})

			return { negativeBottomTops, nearestTopTopPusher }
		},
		{ negativeBottomTops: {}, nearestTopTopPusher: {} }
	)

const getGridDataForMesh = (
	container: Component,
	gridComponents: Array<Component>,
	marginsOptions: MeshMarginsOptions
): DataForMesh => {
	const compsMinTop = _(gridComponents).map(top).min()
	const compsMaxBottom = _(gridComponents).map(bottom).max()
	// @ts-ignore
	const verticalArea = getGridVerticalArea(container, compsMinTop, compsMaxBottom)
	const gridOverflow = getGridOverflow(container, verticalArea)

	const { nearestTopTopPusher, negativeBottomTops } = getCompsPushingData(gridComponents)
	const sortedGridPointsData = calculateGridPoints(gridComponents, negativeBottomTops)
	const compsGridData = calculateCompsGridData(
		sortedGridPointsData,
		verticalArea,
		nearestTopTopPusher,
		marginsOptions
	)
	// @ts-ignore
	const numberOfRows = _(compsGridData).map('rowEnd').max() - 1

	return {
		verticalArea,
		numberOfRows,
		compsGridData,
		negativeBottomTops,
		overflow: gridOverflow,
		containerMinHeight: childrenContainerMinHeight(
			container,
			gridComponents,
			// @ts-ignore
			compsMaxBottom,
			gridOverflow,
			marginsOptions
		),
	}
}

const getAbsoluteContainerMeshData = (container: Component) => {
	const containerMeshData = createEmptyContainerMeshData(
		container,
		// @ts-ignore
		unitize(_.max([container.height, container.minHeight]))
	)
	// @ts-ignore
	const compsStyles = []
	_(container.components).forEach((child) => {
		const style = getCompAbsoluteStyle(child, container)
		compsStyles.push({
			id: child.id,
			style,
		})
	})
	// @ts-ignore
	_.forEach(compsStyles, addChildToMesh.bind(null, containerMeshData))

	return containerMeshData
}

export function createMeshData(container: Component, meshOptions: MeshDataOptions): FlatMeshData {
	const nonAbsChildren = _.reject(container.components, 'absolute')
	const gridComponents = _(nonAbsChildren)
		.map((comp) => (isRotated(comp) ? _.assign({}, comp, getComponentBoundingBox(comp)) : comp))
		.value()

	try {
		const {
			numberOfRows,
			verticalArea,
			containerMinHeight,
			overflow,
			negativeBottomTops,
			compsGridData,
		} = getGridDataForMesh(container, gridComponents, meshOptions)

		const containerMeshData = _.isEmpty(gridComponents)
			? createEmptyContainerMeshData(container, containerMinHeight)
			: createContainerMeshData(container, numberOfRows, containerMinHeight, overflow, meshOptions)
		addComponentsToMeshData(
			container,
			containerMeshData,
			// @ts-ignore
			container.components,
			compsGridData,
			verticalArea,
			meshOptions,
			negativeBottomTops
		)
		addWedgesToMeshData(containerMeshData, compsGridData, meshOptions)

		return containerMeshData
	} catch (e) {
		if (e instanceof RowsLimitError) {
			return getAbsoluteContainerMeshData(container)
		}
		throw e
	}
}

export const structure2mesh = (
	container: Component,
	{
		isRepeatSupported = false,
		cssGridVariant = 'standard',
		softMargin = 10,
		minLockMargin = 70,
		shouldAddNonRotatedComponents = false,
	}: MeshOptions = {}
): Mesh => {
	if (_.isUndefined(container.id)) {
		container = _.defaults({ id: 'root' }, container)
	}

	// TODO: YotamB 09/07/2018 - maybe return flat structure?
	const { hierarchy, styles } = createMeshData(container, {
		isRepeatSupported,
		cssGridVariant,
		softMargin,
		minLockMargin,
		shouldAddNonRotatedComponents,
	})
	return {
		// @ts-ignore
		structure: getRootStructure(hierarchy, container.id),
		styles,
	}
}
